// src/app/videos/VideoGate.tsx
//
// The soft gate, decided in the browser so the page around it can be STATIC.
//
// The page ships one HTML file to everyone: the video, the first worked problem, and this
// component with props that are all already public on the page (a topic, a count, a path).
// Nothing gated is in that HTML or in any client prop. Once the browser knows its session:
//
//   anonymous  → the sign-up card, and no request is made
//   signed in  → one fetch to /api/videos/[collection]/[slug], which re-checks the session
//                on the server and returns the remaining problems, takeaway and exits
//
// So the API is the only place gated text ever comes from, and a static page cannot leak
// what it never had. If the API answers 401 anyway (the session expired since the page
// loaded) the card is shown — the visitor is, in effect, signed out.

'use client'

import { askAboutVideo } from '@/lib/tutor/on-screen-material'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import type { UnlockedVideo } from '@/lib/content/video-catalog'
import ProblemCard from './ProblemCard'
import SignupGateCard from './SignupGateCard'

type Props = {
  collection: string
  slug: string
  topic: string
  /** For the "Ask Sage about this problem" buttons' label. */
  title: string
  /** How many worked problems are behind the gate — the card's copy. */
  hiddenCount: number
  /** Where signup / sign-in land the visitor back: this video's path. */
  returnTo: string
}

type Fetched =
  | { kind: 'pending' }
  | { kind: 'unlocked'; data: UnlockedVideo }
  | { kind: 'signed-out' }
  | { kind: 'failed' }

export const unlockUrl = (collection: string, slug: string) =>
  `/api/videos/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}`

export default function VideoGate({
  collection,
  slug,
  topic,
  title,
  hiddenCount,
  returnTo,
}: Props) {
  const { status } = useSession()
  const [fetched, setFetched] = useState<Fetched>({ kind: 'pending' })
  const [attempt, setAttempt] = useState(0)
  // True once THIS component has hydrated. Until then we render exactly what the server did —
  // the skeleton — whatever the session says. The session can resolve while React is still
  // hydrating the tree above us (cold first load, slow chunks), and switching to the card
  // mid-hydration mismatches the server HTML (React #418). Seen once on a cold load; cheap to
  // rule out for good.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (status !== 'authenticated') return
    let cancelled = false
    setFetched({ kind: 'pending' })
    fetch(unlockUrl(collection, slug), { cache: 'no-store', credentials: 'same-origin' })
      .then(async (res): Promise<Fetched> => {
        if (res.status === 401) return { kind: 'signed-out' }
        if (!res.ok) return { kind: 'failed' }
        return { kind: 'unlocked', data: (await res.json()) as UnlockedVideo }
      })
      .catch((): Fetched => ({ kind: 'failed' }))
      .then((next) => {
        if (!cancelled) setFetched(next)
      })
    return () => {
      cancelled = true
    }
  }, [status, collection, slug, attempt])

  if (!hydrated) return <GateSkeleton />
  if (status === 'unauthenticated' || fetched.kind === 'signed-out') {
    return (
      <div className="mt-6">
        <SignupGateCard returnTo={returnTo} hiddenCount={hiddenCount} topic={topic} />
      </div>
    )
  }
  if (status === 'loading' || fetched.kind === 'pending') return <GateSkeleton />
  if (fetched.kind === 'failed') return <RetryNote onRetry={() => setAttempt((a) => a + 1)} />
  return <Unlocked data={fetched.data} topic={topic} sageRef={{ collection, slug, title }} />
}

/** The rest of the list, the takeaway, and the two exits: Learn by topic, Practise by trick. */
function Unlocked({
  data,
  topic,
  sageRef,
}: {
  data: UnlockedVideo
  topic: string
  sageRef: { collection: string; slug: string; title: string }
}) {
  return (
    <>
      {data.problems.length > 0 && (
        <div className="mt-4 space-y-4">
          {/* These are the LOCKED problems — the page's free one is Problem 1. */}
          {data.problems.map((p, i) => (
            <ProblemCard key={p.given} p={p} ask={askAboutVideo(sageRef, i + 1)} />
          ))}
        </div>
      )}
      <p className="mt-5 border-l-2 border-brand pl-3 text-sm italic text-foreground/90">
        {data.takeaway}
      </p>

      {/* ---------- the link into learning: Learn the topic, practise the trick ---------- */}
      <div className="mt-7 flex flex-wrap gap-3">
        {data.lessonHref && (
          <Link
            href={data.lessonHref}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-on-brand transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
          >
            Learn: {topic}
          </Link>
        )}
        {data.practice && (
          <Link
            href={data.practice.href}
            className="inline-flex items-center gap-2 rounded-lg border border-brand px-4 py-2.5 text-sm font-semibold text-brand-ink transition hover:bg-brand/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
          >
            {data.practice.label}
          </Link>
        )}
      </div>
      {/* Topic practice is labelled as that: it is not questions on this video's trick. */}
      {data.practice?.note && (
        <p className="mt-2 text-xs text-muted-foreground">{data.practice.note}</p>
      )}
    </>
  )
}

/**
 * Shown while the session is unknown (a few hundred ms after load). Sized like the sign-up
 * card most visitors will get, not like the unlocked list — the common case should not jump.
 */
function GateSkeleton() {
  return (
    <div
      className="mt-6 rounded-xl border border-border bg-card/40 p-5"
      role="status"
      aria-label="Loading"
    >
      <div className="space-y-3 motion-safe:animate-pulse">
        <div className="h-4 w-1/3 rounded bg-muted" />
        <div className="h-3 w-full rounded bg-muted" />
        <div className="h-3 w-5/6 rounded bg-muted" />
        <div className="mt-2 h-9 w-full rounded bg-muted" />
      </div>
    </div>
  )
}

function RetryNote({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      className="mt-6 rounded-lg border border-border bg-card/40 p-4 text-sm text-muted-foreground"
      role="alert"
    >
      Couldn&apos;t load the rest of this page.{' '}
      <button
        type="button"
        onClick={onRetry}
        className="font-semibold text-brand-ink underline decoration-brand/60 hover:decoration-brand"
      >
        Try again
      </button>
    </div>
  )
}
